import type { ContractActionsResponse } from './types';

import {
  type QueryFunctionContext,
  queryOptions,
  skipToken,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';

import { type UUID } from '~/types/common';

import { queryKeysContract } from '../queryKeys';

import { fetchContractActions } from './fetchContractActions';

/**
 * Creates query options for fetching available contract actions.
 *
 * @param params - The query parameters.
 * @param params.contractId - The UUID of the contract to fetch actions for.
 * @param params.options - Optional query options to override defaults.
 * @returns The query options including queryKey, queryFn, and other settings.
 */
export const getContractActionsQueryOptions = <
  TData = ContractActionsResponse,
>({
  contractId,
  options,
}: {
  contractId: UUID | undefined;
  options?: Omit<
    UseQueryOptions<ContractActionsResponse, Error, TData>,
    'queryKey' | 'queryFn'
  >;
}) =>
  queryOptions({
    queryFn: contractId
      ? async ({ signal }: QueryFunctionContext) =>
          fetchContractActions(contractId, { signal })
      : skipToken,
    queryKey: queryKeysContract.getActions(contractId ?? ''),
    staleTime: ms('30s'),
    ...options,
  });

/**
 * Custom hook for fetching available contract actions based on current status.
 * Use this to dynamically render action buttons in the UI.
 *
 * @param contractId - The UUID of the contract to fetch actions for.
 * @param options - Optional query options to override defaults.
 * @returns An object containing the available actions and related query properties.
 * @see https://app.dev.vestigas.com/redoc#tag/Contracts/operation/get_contract_actions_contracts__contract_id__actions_get
 */
export const useQueryContractActions = (
  contractId: UUID | undefined,
  options?: Omit<
    UseQueryOptions<ContractActionsResponse, Error, ContractActionsResponse>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery({
    ...getContractActionsQueryOptions<ContractActionsResponse>({
      contractId,
      options,
    }),
  });
};
